<script>
import { useAlert } from 'dashboard/composables';
import TimeRangeFields from '../components/TimeRangeFields.vue';
import { FIELD_CLASS } from '../constants';
import { timeRangeErrorKey } from '../helpers';
import { reportInvalidForm } from '../formFeedback';
import { apiErrorMessage } from '../../../../api/realEstate/errors';

// Reused for both Layer-0 (project opening hours) and Layer-3 (schedule)
// date overrides — the request/response shape is identical
// (docs/real-estate/be/10-api-reference.md §A7, and §A8 "Same body and
// rules as the Layer-0 overrides"). The parent owns which endpoint gets
// called and passes it in as `save`.
export default {
  name: 'DateOverrideModal',
  components: { TimeRangeFields },
  props: {
    save: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      form: {
        override_date: '',
        start_time: null,
        end_time: null,
        reason: '',
      },
      isSubmitting: false,
      submitted: false,
    };
  },
  computed: {
    fieldErrors() {
      return {
        override_date: !this.form.override_date,
      };
    },
    timesErrorKey() {
      return timeRangeErrorKey(this.form);
    },
    isFormValid() {
      return (
        !Object.values(this.fieldErrors).some(Boolean) && !this.timesErrorKey
      );
    },
  },
  methods: {
    handleTimeRangeChange(value) {
      this.form.start_time = value.start_time;
      this.form.end_time = value.end_time;
    },
    async handleSubmit() {
      this.submitted = true;
      if (!this.isFormValid) {
        reportInvalidForm(this);
        return;
      }
      this.isSubmitting = true;
      try {
        await this.save({
          override_date: this.form.override_date,
          start_time: this.form.start_time,
          end_time: this.form.end_time,
          reason: this.form.reason.trim() || null,
        });
        useAlert(this.$t('REAL_ESTATE.OVERRIDES.SUCCESS_ADDED'));
        this.$emit('close');
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'REAL_ESTATE.OVERRIDES.ERROR_ADD'));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-md max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.OVERRIDES.MODAL_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4" novalidate @submit.prevent="handleSubmit">
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.OVERRIDES.FIELD_DATE') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.override_date"
            type="date"
            class="reset-base mb-0"
            :class="[
              fieldClass,
              {
                '!border-red-500': submitted && fieldErrors.override_date,
              },
            ]"
            required
          />
          <p
            v-if="submitted && fieldErrors.override_date"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
          </p>
        </div>

        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.OVERRIDES.FIELD_HOURS') }}
          </label>
          <TimeRangeFields
            :value="{ start_time: form.start_time, end_time: form.end_time }"
            :all-day-label="$t('REAL_ESTATE.OVERRIDES.FIELD_CLOSED_ALL_DAY')"
            @input="handleTimeRangeChange"
          />
          <p
            v-if="submitted && timesErrorKey"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t(timesErrorKey) }}
          </p>
        </div>

        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.OVERRIDES.FIELD_REASON') }}
          </label>
          <input
            v-model="form.reason"
            :placeholder="$t('REAL_ESTATE.OVERRIDES.FIELD_REASON_PLACEHOLDER')"
            type="text"
            class="reset-base mb-0"
            :class="fieldClass"
          />
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.OVERRIDES.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.OVERRIDES.SAVE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
